import { HStack, Text } from '@ayble-health/dashboard-components';
import styled from '@emotion/styled';
import { Box } from '@chakra-ui/react';
import { IconButton } from '../../Form/IconButton';
import { MoreHorizontal, Trash2 } from 'lucide-react';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const Draggable = styled('div')<{ transform?: string; transition?: string }>`
  transform: ${({ transform }) => transform};
  transition: ${({ transition }) => transition};
  margin: 4px 0;

  & .drag-handle:hover {
    cursor: grab;
  }
`;

type Props = {
  deletable?: boolean;
  diagnosis: string;
  draggable?: boolean;
  handleDelete: () => void;
  id: string;
  index: number;
};

const SortableDxItem = ({
  deletable = false,
  diagnosis,
  draggable = false,
  handleDelete,
  id,
  index,
}: Props) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const cannotChange = !deletable && index <= 1;
  const bgColor = cannotChange && !draggable ? 'cloud.200' : 'white';

  return (
    <Draggable
      ref={setNodeRef}
      transform={style.transform}
      transition={style.transition}
      {...attributes}
    >
      <Box
        borderWidth={1}
        justifyContent="center"
        backgroundColor={bgColor}
        borderColor={'cloud.200'}
        w={'100%'}
        p={2}
        pr={4}
        rounded={8}
        minH={'44px'}
      >
        <HStack alignItems={'center'}>
          {draggable && (
            <Box
              {...listeners}
              borderWidth={1}
              mr={2}
              w={5}
              py={1}
              rounded={4}
              borderColor={'transparent'}
            >
              <span className={'drag-handle'}>
                <HStack>
                  <Box transform={'rotate(90deg)'}>
                    <MoreHorizontal color={'secondary.200'} size={16} />
                  </Box>
                  <Box ml={-1.5} transform={'rotate(90deg)'}>
                    <MoreHorizontal color={'secondary.200'} size={16} />
                  </Box>
                </HStack>
              </span>
            </Box>
          )}

          <Text
            fontSize={16}
            fontWeight={700}
            lineHeight={'125%'}
            color={'secondary.200'}
          >
            {diagnosis}
          </Text>

          {deletable && (
            <Box ml={'auto'}>
              <IconButton onClick={handleDelete}>
                <Trash2 color={'secondary.200'} size={16} />
              </IconButton>
            </Box>
          )}

          {cannotChange && (
            <Box rounded="full" bgColor={'#FFDA67'} py={1} px={2} ml={'auto'}>
              <Text
                fontSize={'12px'}
                lineHeight={'125%'}
                fontWeight={400}
                color={'secondary.500'}
              >
                {index === 0 ? 'Primary' : 'Secondary'}
              </Text>
            </Box>
          )}
        </HStack>
      </Box>
    </Draggable>
  );
};

export default SortableDxItem;
